/**
 * What a verification attempt should tell the operator.
 *
 * Extracted from `Channels.tsx` so it can be asserted. The decision it makes is
 * small and the consequence is not: whether the screen says a channel FAILED,
 * or says nothing was attempted. Those are different facts about different
 * things — one is about the channel, the other about this deployment — and the
 * screen said the first for both until it was split out.
 *
 * It lives here rather than in the component because a React handler is the one
 * place in this codebase nothing can reach: the browser pane would not surface
 * the banner for me to look at, so the choice behind it was unverifiable while
 * it stayed inline. This is the half that can be proved.
 */

import { ApiError } from './api.ts';

export type VerifyOutcome =
  /** A message was sent and accepted. */
  | { kind: 'verified'; text: string }
  /**
   * Nothing was attempted, because this deployment has no transport for the
   * kind. Says nothing about the channel, so it must not read as a failure.
   */
  | { kind: 'untried'; text: string }
  /** A transport tried and the far end refused. That IS about the channel. */
  | { kind: 'failed'; text: string };

/**
 * Read the server's answer.
 *
 * The API already separates the two failure modes — 400 carrying `untried` when
 * no transport exists for the kind, 502 when one tried and was refused. This
 * reads that flag rather than the status code: the status is transport-level
 * and could be changed by a proxy or a future route, while the flag is the
 * server stating which of the two it meant.
 *
 * `sms`, `slack` and `teams` are all valid channel kinds with no transport, so
 * `untried` is the ordinary answer for three of the five and not an edge case.
 */
export function verifyOutcome(label: string, error: unknown): VerifyOutcome {
  if (error === null || error === undefined) {
    return { kind: 'verified', text: `${label}: a test message was sent and accepted.` };
  }

  const message = error instanceof ApiError ? error.message : String(error);
  const body = error instanceof ApiError ? (error.body as { untried?: unknown } | null) : null;

  if (body?.untried === true) {
    return {
      kind: 'untried',
      text:
        `${label}: not verified — ${message} This says nothing about whether the ` +
        `channel works; it cannot be proved from this deployment.`,
    };
  }

  return { kind: 'failed', text: `${label}: ${message}` };
}
